/**
 * Payload builders for the enrollment and payment events. Send them with
 * `trackEvent`, which names the event and checks the payload fits it:
 *   trackEvent(AnalyticsEvent.SelectProgram, programParams(program))
 *
 * ALX funnel:
 *   program_detail_view → enrollment_started →
 *   first_program_enrolled (or program_enrolled after the first)
 * set-edu (GSA) funnel:
 *   view_programs → select_program → subscription_plan_selected →
 *   [payment_initiated → payment_successful] →
 *   first_program_enrolled / program_enrolled (plan_type: trial | paid)
 */

import {
  AnalyticsEvent,
  EcommerceParams,
  EnrolledParams,
  PaymentParams,
  PlanType,
  ProgramParams,
} from './dataLayer';

export interface AnalyticsProgram {
  id: string;
  name: string;
  abbreviation?: string | null;
}

/** A plan at the interval the learner picked. */
export interface AnalyticsPlan {
  /** `free` for staying on the free tier. */
  id: string;
  name: string;
  interval?: string | null;
  /** In major units (12.5, not 1250). */
  value: number;
  currency?: string | null;
}

interface EnrollingUser {
  sandId?: string | null;
  programEnrollments?: Array<{
    id: string;
    program?: { id: string } | null;
  }> | null;
}

export const programParams = (program: AnalyticsProgram): ProgramParams => ({
  program_id: program.id,
  program_name: program.name,
  program_abbreviation: program.abbreviation,
});

/**
 * `subscription_plan_selected`. `program` is the one the learner came to
 * start, absent when they reached the plans from the banner or Settings.
 */
export const planParams = (
  plan: AnalyticsPlan,
  program?: AnalyticsProgram | null
): PaymentParams => ({
  plan_id: plan.id,
  plan_name: plan.name,
  billing_interval: plan.interval,
  value: plan.value,
  currency: plan.currency?.toUpperCase(),
  ...(program ? programParams(program) : {}),
});

/**
 * `payment_initiated` (Stripe's checkout opens) and `payment_successful` (it
 * reports the payment done). Both carry GA4's ecommerce object keyed by the
 * checkout session, so the FIT container can send them as `begin_checkout` /
 * `purchase` (GA4 drops a repeat purchase with the same `transaction_id`).
 */
export const paymentParams = (
  checkoutSessionId: string,
  plan: AnalyticsPlan,
  program?: AnalyticsProgram | null
): PaymentParams & EcommerceParams => ({
  ...planParams(plan, program),
  ecommerce: {
    transaction_id: checkoutSessionId,
    value: plan.value,
    currency: plan.currency?.toUpperCase(),
    items: [
      {
        item_id: plan.id,
        item_name: plan.name,
        item_variant: plan.interval,
        price: plan.value,
        quantity: 1,
      },
    ],
  },
});

/**
 * Which enrolled event to send: `first_program_enrolled` when this is the
 * learner's only enrollment, `program_enrolled` otherwise - never both, so
 * each enrollment counts once. `user` is the `applyToProgram.user` result.
 */
export const enrolledEvent = (user?: EnrollingUser | null) =>
  user?.programEnrollments?.length === 1
    ? AnalyticsEvent.FirstProgramEnrolled
    : AnalyticsEvent.ProgramEnrolled;

/** `planType` is set-edu only. */
export const enrolledParams = (
  program: AnalyticsProgram,
  user?: EnrollingUser | null,
  planType?: PlanType
): EnrolledParams => ({
  ...programParams(program),
  plan_type: planType,
  program_enrollment_id: user?.programEnrollments?.find(
    (pe) => pe.program?.id === program.id
  )?.id,
  // Left out rather than sent empty, which would wipe the `user_id` that
  // `setDataLayerUser` keeps in the data layer.
  ...(user?.sandId ? { user_id: user.sandId } : {}),
});
